<template>
    <div class="w-full">
        <!-- History tab mobile cards -->
        <div
            v-if="currentTab === 'transfer-bonus-history'"
            class="lg:hidden flex flex-col gap-4 w-full"
        >
            <div
                v-for="(partner, idx) in historyRows"
                :key="
                    partner.bonus_id ||
                    partner.destination_program_id +
                        '-' +
                        (partner.bonus_end_date || idx)
                "
                class="flex flex-col p-4 gap-4 rounded-lg bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700"
            >
                <div class="flex items-start gap-2 justify-between">
                    <div class="flex flex-col items-start gap-1">
                        <h4
                            class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                        >
                            {{ partner.destination_program_name }}
                        </h4>
                        <span class="text-slate-500 dark:text-slate-400 text-sm leading-5">
                            {{
                                formatProgramType(
                                    partner.destination_program_type
                                )
                            }}
                        </span>
                    </div>
                    <div
                        class="flex w-[84px] h-[36px] justify-center items-center flex-shrink-0"
                    >
                        <img
                            v-if="partner.destination_program_image"
                            :src="`${cdnUrl}/${partner.destination_program_image.path}/_small/${partner.destination_program_image.file_name}`"
                            :alt="partner.destination_program_name"
                            class="max-w-full max-h-full object-contain"
                        />
                        <span
                            v-else
                            class="text-xs text-slate-500 dark:text-slate-400 font-medium flex items-center justify-center w-full h-full"
                        >
                            {{
                                partner.destination_program_name
                                    .substring(0, 2)
                                    .toUpperCase()
                            }}
                        </span>
                    </div>
                </div>

                <div class="flex flex-col gap-3">
                    <div class="flex h-6 items-center gap-6 self-stretch">
                        <div class="flex items-center gap-2 [flex:1_0_0]">
                            <Icon
                                icon="transfer-rate"
                                class="w-5 h-5 text-slate-500 dark:text-slate-400"
                            />
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex-1"
                            >
                                Transfer rate
                            </span>
                        </div>
                        <div class="flex items-center gap-3 [flex:1_0_0]">
                            <template
                                v-if="
                                    partner.bonus_multiplier &&
                                    partner.bonus_multiplier > 1
                                "
                            >
                                <span
                                    class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5 line-through"
                                >
                                    {{ partner.base_transfer_ratio }}
                                </span>
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    {{ partner.effective_transfer_ratio }}
                                </span>
                            </template>
                            <template v-else>
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    {{ partner.transfer_ratio }}
                                </span>
                            </template>
                        </div>
                    </div>

                    <div
                        v-if="partner.bonus_percentage != null"
                        class="flex h-6 items-center gap-6 self-stretch"
                    >
                        <div class="flex items-center gap-2 [flex:1_0_0]">
                            <Icon
                                icon="gift"
                                class="w-5 h-[21px] text-slate-500 dark:text-slate-400"
                            />
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex-1"
                            >
                                Bonus
                            </span>
                        </div>
                        <div class="flex items-center gap-3 [flex:1_0_0]">
                            <span
                                class="inline-flex py-[2px] px-3 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 text-center"
                            >
                                +{{ partner.bonus_percentage }}%
                                <template
                                    v-if="
                                        partner.status === 'active' &&
                                        partner.bonus_end_date
                                    "
                                >
                                    &nbsp;until
                                    {{
                                        formatDateShort(
                                            partner.bonus_end_date
                                        )
                                    }}
                                </template>
                            </span>
                        </div>
                    </div>

                    <div class="flex h-6 items-center gap-6 self-stretch">
                        <div class="flex items-center gap-2 [flex:1_0_0]">
                            <Icon
                                icon="clock"
                                class="w-5 h-[21px] text-slate-500 dark:text-slate-400"
                            />
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex-1"
                            >
                                End date
                            </span>
                        </div>
                        <div class="flex items-center gap-3 [flex:1_0_0]">
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                {{ formatDate(partner.bonus_end_date) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Current rates tab mobile cards -->
        <div
            v-if="currentTab === 'current-rates'"
            class="lg:hidden flex flex-col gap-4 w-full"
        >
            <div
                v-for="partner in visibleMobilePartners"
                :key="partner.id"
                class="flex flex-col p-4 gap-4 rounded-lg bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700"
            >
                <div class="flex items-start gap-2 justify-between">
                    <div class="flex flex-col items-start gap-1">
                        <h4
                            class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                        >
                            {{ partner.destination_program_name }}
                        </h4>
                        <span class="text-slate-500 dark:text-slate-400 text-sm leading-5">
                            {{
                                formatProgramType(
                                    partner.destination_program_type
                                )
                            }}
                        </span>
                    </div>
                    <div
                        class="flex w-[84px] h-[36px] justify-center items-center flex-shrink-0"
                    >
                        <img
                            v-if="partner.destination_program_image"
                            :src="`${cdnUrl}/${partner.destination_program_image.path}/_small/${partner.destination_program_image.file_name}`"
                            :alt="partner.destination_program_name"
                            class="max-w-full max-h-full object-contain"
                        />
                        <span
                            v-else
                            class="text-xs text-slate-500 dark:text-slate-400 font-medium flex items-center justify-center w-full h-full"
                        >
                            {{
                                partner.destination_program_name
                                    .substring(0, 2)
                                    .toUpperCase()
                            }}
                        </span>
                    </div>
                </div>

                <div class="flex flex-col gap-3">
                    <div class="flex h-6 items-center gap-6 self-stretch">
                        <div class="flex items-center gap-2 [flex:1_0_0]">
                            <Icon
                                icon="transfer-rate"
                                class="w-5 h-5 text-slate-500 dark:text-slate-400"
                            />
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex-1"
                            >
                                Transfer rate
                            </span>
                        </div>
                        <div class="flex items-center gap-3 [flex:1_0_0]">
                            <template
                                v-if="
                                    partner.bonus_multiplier &&
                                    partner.bonus_multiplier > 1
                                "
                            >
                                <span
                                    class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5 line-through"
                                >
                                    {{ partner.base_transfer_ratio }}
                                </span>
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    {{ partner.effective_transfer_ratio }}
                                </span>
                            </template>
                            <template v-else>
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    {{ partner.transfer_ratio }}
                                </span>
                            </template>
                            <TransferRateBreakdown :rates="partner.rates" />
                        </div>
                    </div>

                    <div
                        v-if="
                            partner.bonus_multiplier &&
                            partner.bonus_multiplier > 1
                        "
                        class="flex h-6 items-center gap-6 self-stretch"
                    >
                        <div class="flex items-center gap-2 [flex:1_0_0]">
                            <Icon
                                icon="gift"
                                class="w-5 h-[21px] text-slate-500 dark:text-slate-400"
                            />
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex-1"
                            >
                                Bonus
                            </span>
                        </div>
                        <div class="flex items-center gap-3 [flex:1_0_0]">
                            <span
                                class="inline-flex py-[2px] px-3 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 text-center"
                            >
                                +{{ partner.bonus_percentage }}%
                                <template v-if="partner.bonus_end_date">
                                    until
                                    {{
                                        formatDateShort(partner.bonus_end_date)
                                    }}
                                </template>
                            </span>
                        </div>
                    </div>

                    <div class="flex h-6 items-center gap-6 self-stretch">
                        <div class="flex items-center gap-2 [flex:1_0_0]">
                            <Icon
                                :icon="
                                    isImmediateTransfer(
                                        partner.transfer_time
                                    )
                                        ? 'clock-fast'
                                        : 'clock'
                                "
                                class="w-5 h-[21px] text-slate-500 dark:text-slate-400"
                            />
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex-1"
                            >
                                Transfer time
                            </span>
                        </div>
                        <div class="flex items-center gap-3 [flex:1_0_0]">
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                {{
                                    formatTransferTimeText(
                                        partner.transfer_time
                                    )
                                }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="pt-1 relative">
                    <button
                        @click="$emit('open-calculator', partner)"
                        @mouseover="partner.showCalculatorTooltip = true"
                        @mouseleave="partner.showCalculatorTooltip = false"
                        class="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium"
                    >
                        <Icon
                            icon="calculator"
                            class="w-4 h-4 text-slate-600 dark:text-slate-300"
                        />
                        <span>Calculate points</span>
                    </button>
                    <div
                        v-show="partner.showCalculatorTooltip"
                        class="hidden md:block absolute top-full mt-2 left-1/2 -translate-x-1/2 py-1 px-2 text-sm text-white bg-[#1E293B] rounded-lg shadow-sm whitespace-nowrap z-50"
                    >
                        Points calculator
                    </div>
                </div>
            </div>
            <div
                v-if="remainingMobilePartners > 0"
                class="flex justify-center pt-2"
            >
                <button
                    @click="$emit('toggle-mobile-expand')"
                    class="flex h-10 px-4 items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                    <span>{{ mobilePartnersToggleLabel }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import TransferRateBreakdown from '@/components/transfer-partners/TransferRateBreakdown.vue';

const cdnUrl = window.cdnUrl;

defineProps({
    currentTab: String,
    historyRows: Array,
    visibleMobilePartners: Array,
    remainingMobilePartners: Number,
    mobilePartnersToggleLabel: String,
    formatDate: Function,
    formatDateShort: Function,
    formatProgramType: Function,
    formatTransferTimeText: Function,
    isImmediateTransfer: Function,
});

defineEmits(['open-calculator', 'toggle-mobile-expand', 'update:openBonusAlert']);
</script>
